import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "react-toastify";
import { passwordClipboardProps } from "../utils/passwordClipboard";

const validateNewPassword = (password) => {
  if (!password) return "New password is required.";
  if (password.length < 8) return "Password must be at least 8 characters.";
  if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
    return "Include at least one uppercase letter, one lowercase letter, and one special character.";
  }
  return "";
};

const validateConfirmPassword = (newPassword, confirmPassword) => {
  if (!confirmPassword) return "Please confirm your new password.";
  if (newPassword !== confirmPassword) return "New password and confirm password must match.";
  return "";
};

const NewPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState(() => new URLSearchParams(location.search).get("token") || "");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("token")) return;

    setToken(params.get("token") || "");
    params.delete("token");
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash }, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [otpError, setOtpError] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({ newPassword: "", confirmPassword: "" });
  const [hasSubmittedPassword, setHasSubmittedPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!showOtpModal || resendSeconds === 0) return;
    const timer = window.setTimeout(() => setResendSeconds((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [showOtpModal, resendSeconds]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...formData, [name]: value };
    setFormData(updatedData);
    setError("");
    if (hasSubmittedPassword) {
      setFieldErrors({
        newPassword: validateNewPassword(updatedData.newPassword),
        confirmPassword: validateConfirmPassword(updatedData.newPassword, updatedData.confirmPassword),
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || otpLoading || success) return;
    setSuccess("");
    setError("");
    setHasSubmittedPassword(true);

    if (!token) {
      setError("This password link is missing a token. Please request a new link.");
      toast.error("This password link is missing a token. Please request a new link.");
      return;
    }

    const nextFieldErrors = {
      newPassword: validateNewPassword(formData.newPassword),
      confirmPassword: validateConfirmPassword(formData.newPassword, formData.confirmPassword),
    };
    setFieldErrors(nextFieldErrors);
    if (nextFieldErrors.newPassword || nextFieldErrors.confirmPassword) {
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/user/set-password/request-otp`, {
        token,
        new_password: formData.newPassword,
        confirm_password: formData.confirmPassword,
      });
      setOtp("");
      setOtpError("");
      setShowOtpModal(true);
      setResendSeconds(45);
    } catch (err) {
      const message = err.response?.data?.message || "Unable to send the verification code. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpLoading || resendLoading || resendSeconds > 0) return;
    setResendLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/user/set-password/request-otp`, {
        token,
        new_password: formData.newPassword,
        confirm_password: formData.confirmPassword,
      });
      setOtp("");
      setOtpError("");
      setResendSeconds(45);
      toast.success("A new verification code has been sent.");
    } catch (err) {
      const message = err.response?.data?.message || "Unable to resend the code. Please try again.";
      setOtpError(message);
      toast.error(message);
    } finally {
      setResendLoading(false);
    }
  };

  const handleOtpChange = (e) => {
    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
    if (otpError) setOtpError("");
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (otpLoading || resendLoading || success) return;

    if (!/^\d{6}$/.test(otp)) {
      const message = "Enter the 6-digit code sent to your email.";
      setOtpError(message);
      toast.warn(message);
      return;
    }

    setOtpLoading(true);
    setOtpError("");

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/user/set-password/verify-otp`, {
        token,
        otp,
      });
      setSuccess(response.data?.message || "Your password has been set successfully.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      const message = err.response?.data?.message || "The verification code is invalid or has expired. Please try again.";
      setOtpError(message);
      toast.error(message);
    } finally {
      setOtpLoading(false);
    }
  };

  const closeOtpModal = () => {
    if (otpLoading || resendLoading || success) return;
    setShowOtpModal(false);
    setOtpError("");
  };

  return (
    <div id="wrapper">
      <section className="signup-section login-section d-flex align-items-center py-5 py-md-0">
        <div className="container signup-container">
          <div className="row">
            <div className="col-md-6 text-white d-flex align-items-center pe-md-5">
              <div className="pe-md-5 text-center text-md-start w-100">
                <h1 className="fw-bold mb-4">Set your password</h1>
                <p className="color858c95 ft-18 lh-27 inter">Choose a strong password to keep your account secure.</p>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-container mt-md-0 d-flex align-items-center justify-content-center mt-5">
                <div className="w-100">
                  <h2 className="ft-28 fw-bold mb-1 textred">Set Password</h2>
                  <p className="color858c95 ft-14 lh-27 inter">Enter and confirm your new password below.</p>

                  {success && !showOtpModal && (
                    <div className="alert alert-success text-center" role="status">
                      {success}
                    </div>
                  )}
                  {error && (
                    <div className="alert alert-danger text-center" role="alert">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} noValidate>
                    <div className="form-group">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        className={`form-control password-with-toggle ${formData.newPassword ? "has-value" : ""} ${fieldErrors.newPassword ? "is-invalid" : ""}`}
                        id="newPassword"
                        name="newPassword"
                        placeholder=" "
                        autoComplete="new-password"
                        aria-invalid={Boolean(fieldErrors.newPassword)}
                        aria-describedby={fieldErrors.newPassword ? "new-password-error" : "password-help"}
                        value={formData.newPassword}
                        onChange={handleChange}
                        {...passwordClipboardProps}
                      />
                      <label htmlFor="newPassword">New Password</label>
                      <button
                        type="button"
                        className="password-visibility-toggle"
                        aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                        aria-pressed={showNewPassword}
                        onClick={() => setShowNewPassword((visible) => !visible)}
                      >
                        {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    <p id="password-help" className="color858c95 ft-11">
                      Use at least 8 characters, including uppercase and lowercase letters and a special character.
                    </p>
                    {fieldErrors.newPassword && (
                      <div id="new-password-error" className="invalid-feedback d-block" role="alert">
                        {fieldErrors.newPassword}
                      </div>
                    )}

                    <div className="form-group">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        className={`form-control password-with-toggle ${formData.confirmPassword ? "has-value" : ""} ${fieldErrors.confirmPassword ? "is-invalid" : ""}`}
                        id="confirmPassword"
                        name="confirmPassword"
                        placeholder=" "
                        autoComplete="new-password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        {...passwordClipboardProps}
                        aria-invalid={Boolean(fieldErrors.confirmPassword)}
                        aria-describedby={fieldErrors.confirmPassword ? "confirm-password-error" : undefined}
                      />
                      <label htmlFor="confirmPassword">Confirm Password</label>
                      <button
                        type="button"
                        className="password-visibility-toggle"
                        aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                        aria-pressed={showConfirmPassword}
                        onClick={() => setShowConfirmPassword((visible) => !visible)}
                      >
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                      {fieldErrors.confirmPassword && (
                        <div id="confirm-password-error" className="invalid-feedback d-block" role="alert">
                          {fieldErrors.confirmPassword}
                        </div>
                      )}
                    </div>

                    <button type="submit" className="btn btn-red ft-15 fw-600 w-100 my-3 py-3" disabled={loading || Boolean(success)}>
                      {loading ? "Setting Password..." : "Set Password"} <i className="fa-solid fa-arrow-right ms-2"></i>
                    </button>

                    <p className="mt-3 ft-14 lh-24 text-center">
                      Remember your password?
                      <Link to="/login" className="Register-link textred" style={{ marginLeft: "5px" }}>
                        Sign In
                      </Link>
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showOtpModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="password-otp-title"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.65)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 rounded-4 shadow">
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <div>
                  <h2 id="password-otp-title" className="modal-title ft-28 fw-bold textred">
                    Verify your email
                  </h2>
                  <p className="color858c95 ft-14 lh-24 inter mb-0 mt-2">
                    Enter the 6-digit OTP sent to your registered email address.
                  </p>
                </div>
                {!success && (
                  <button type="button" className="btn-close align-self-start" aria-label="Close" onClick={closeOtpModal}></button>
                )}
              </div>

              <div className="modal-body px-4 pb-4 pt-3">
                {success ? (
                  <div className="alert alert-success text-center mb-0" role="status">
                    {success} Redirecting to sign in...
                  </div>
                ) : (
                  <form onSubmit={handleOtpSubmit} noValidate>
                    {otpError && (
                      <div className="alert alert-danger text-center" role="alert">
                        {otpError}
                      </div>
                    )}

                    <div className="form-group mb-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        className={`form-control ${otp ? "has-value" : ""} ${otpError ? "is-invalid" : ""}`}
                        id="passwordOtp"
                        name="passwordOtp"
                        placeholder=" "
                        value={otp}
                        onChange={handleOtpChange}
                        maxLength="6"
                        pattern="[0-9]{6}"
                        aria-invalid={Boolean(otpError)}
                        autoFocus
                        required
                      />
                      <label htmlFor="passwordOtp">6-digit OTP</label>
                    </div>

                    <button type="submit" className="btn btn-red ft-15 fw-600 w-100 mt-3 py-3" disabled={otpLoading || resendLoading || otp.length !== 6}>
                      {otpLoading ? "Verifying..." : "Verify OTP"} <i className="fa-solid fa-arrow-right ms-2"></i>
                    </button>
                    <button type="button" className="btn btn-link textred w-100 mt-2" onClick={handleResendOtp} disabled={otpLoading || resendLoading || resendSeconds > 0}>
                      {resendLoading ? "Sending code..." : resendSeconds > 0 ? `Resend code in ${resendSeconds}s` : "Resend code"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewPassword;
